import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as utc from 'dayjs/plugin/utc';

dayjs.extend(customParseFormat);
dayjs.extend(utc);

@Injectable()
/**
 * Utility class for date-related operations.
 */
export class DateUtil {
	/**
	 * The date format used in European models (D/MM/YYYY).
	 */
	public static readonly MODELS_EU_DATE_FORMAT: string = 'D/MM/YYYY';

	/**
	 * The date format used in US models (M/DD/YY).
	 */
	public static readonly MODELS_US_DATE_FORMAT: string = 'M/DD/YY';

	/**
	 * The format for displaying the month and year (MMMM YYYY).
	 */
	public static readonly MMMM_YYYY_FORMAT: string = 'MMMM YYYY';

	/**
	 * The date format used for BFM date numbers (YYYYMMDD).
	 */
	public static readonly BFM_DATE_NUMBER_FORMAT: string = 'YYYYMMDD';

	/**
	 * The date format with dashes (YYYY-MM-DD).
	 */
	public static readonly DATE_DASHES_FORMAT: string = 'YYYY-MM-DD';

	/**
	 * Converts a date to BFM date format.
	 * @param date The date to convert.
	 * @returns The date in BFM date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static convertToBfmDate(date: Date): string {
		if (date) {
			return dayjs(date, this.MODELS_US_DATE_FORMAT).format(DateUtil.BFM_DATE_NUMBER_FORMAT);
		} else {
			throw new Error('Invalid date passed when converting to BFM date');
		}
	}

	/**
	 * Converts a date to UTC BFM date format.
	 * @param date The date to convert.
	 * @returns The date in UTC BFM date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static convertToUTCBfmDate(date: Date): string {
		if (date) {
			return dayjs.utc(date, this.MODELS_US_DATE_FORMAT).format(DateUtil.BFM_DATE_NUMBER_FORMAT);
		} else {
			throw new Error('Invalid date passed when converting to UTC BFM date');
		}
	}

	/**
	 * Formats a date to US date format.
	 * @param date The date to format.
	 * @returns The formatted date in US date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static formatDate(date: string | number | dayjs.Dayjs): string {
		if (date) {
			const dateToParse = (typeof date === 'string' || typeof date === 'number') ? DateUtil.createDayjsFromDate(date) : date;
			return dayjs(dateToParse, DateUtil.BFM_DATE_NUMBER_FORMAT, true).format(DateUtil.MODELS_US_DATE_FORMAT);
		} else {
			throw new Error('Invalid date passed when formatting to US date');
		}
	}

	/**
	 * Formats a date to European date format.
	 * @param date The date to format.
	 * @returns The formatted date in European date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static formatDateEU(date: dayjs.Dayjs): string {
		if (date) {
			return dayjs(date, 'YYYYMMDD').format(DateUtil.MODELS_EU_DATE_FORMAT);
		} else {
			throw new Error('Invalid date passed when formatting to European date');
		}
	}

	/**
	 * Formats a date to text date format.
	 * @param date The date to format.
	 * @param currentFormat The current format of the date. Defaults to BFM date number format.
	 * @returns The formatted date in text date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static formatDateText(date: dayjs.Dayjs, currentFormat: string = DateUtil.BFM_DATE_NUMBER_FORMAT): string {
		if (date) {
			return dayjs(date, currentFormat).format(DateUtil.MMMM_YYYY_FORMAT);
		} else {
			throw new Error('Invalid date passed when formatting to text date');
		}
	}

	/**
	 * Formats a date with dashes to US date format.
	 * @param date The date with dashes to format.
	 * @returns The formatted date with dashes in US date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static formatDashesDate(date: dayjs.Dayjs): string {
		if (date) {
			return dayjs(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
		} else {
			throw new Error('Invalid date passed when formatting dashes date to US date');
		}
	}

	/**
	 * Formats a date with dashes to European date format.
	 * @param date The date with dashes to format.
	 * @returns The formatted date with dashes in European date format.
	 * @throws Error if an invalid date is passed.
	 */
	public static formatDashesDateEU(date: dayjs.Dayjs): string {
		if (date) {
			return dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
		} else {
			throw new Error('Invalid date passed when formatting dashes date to European format');
		}
	}

	/**
	 * Formats a date with dashes to US format.
	 * @param date The date with dashes to format.
	 * @returns The formatted date with dashes in US format.
	 * @throws Error if an invalid date is passed.
	 */
	public static formatDashesDateUS(date: dayjs.Dayjs): string {
		if (date) {
			return dayjs(date, 'YYYY-MM-DD').format(this.MODELS_US_DATE_FORMAT);
		} else {
			throw new Error('Invalid date passed when formatting dashes date to US format');
		}
	}

	/**
	 * Converts a BFM date to a JavaScript Date object.
	 * @param asOfDate The BFM date to convert.
	 * @returns The JavaScript Date object.
	 */
	public static convertBfmDate(asOfDate: number): Date {
		if (asOfDate) {
			const dateStr = asOfDate.toString();
			let longDate;
			if (!dateStr.includes('/')) {
				longDate = new Date(Date.UTC(+dateStr.slice(0, 4), +dateStr.slice(-4, -2) - 1, +dateStr.slice(-2)));
			} else {
				const dateSplitArray = dateStr.split('/');
				longDate = new Date(Date.UTC(+dateSplitArray[2], +dateSplitArray[0] - 1, +dateSplitArray[1]));
			}
			return longDate;
		}
	}

	/**
	 * Calculates the total number of months between two BFM dates.
	 * @param startDate The start date.
	 * @param endDate The end date.
	 * @returns The total number of months between the start and end dates.
	 */
	public static calculateBfmDateTotalMonth(startDate: number, endDate: number): number {
		return dayjs(String(endDate), DateUtil.BFM_DATE_NUMBER_FORMAT).diff(
			dayjs(String(startDate), DateUtil.BFM_DATE_NUMBER_FORMAT),
			'month',
			true
		);
	}

	/**
	 * Creates a Dayjs object from a date value.
	 * @param value The date value.
	 * @returns The Dayjs object.
	 */
	public static createDayjsFromDate(value: number | string): dayjs.Dayjs {
		return dayjs(
			String(value),
			[
				'YYYYMMDD',
				'MM/DD/YYYY',
				DateUtil.MODELS_EU_DATE_FORMAT,
				DateUtil.MODELS_US_DATE_FORMAT,
				DateUtil.MMMM_YYYY_FORMAT,
				DateUtil.BFM_DATE_NUMBER_FORMAT,
				DateUtil.DATE_DASHES_FORMAT
			],
			true
		);
	}

	/**
	 * Rounds the current time to the nearest specified minutes.
	 * @param round The number of minutes to round to.
	 * @returns The rounded time as a Unix timestamp string.
	 */
	public static roundTimeToNearestMinutes(round: number): string {
		if (round) {
			const currentDate = dayjs().utc();
			const roundedMinutes = Math.floor(currentDate.minute() / round) * round;
			return currentDate.minute(roundedMinutes).second(0).millisecond(0).unix().toString();
		}
	}
}
